import type { Address } from "viem";

export interface ProtocolData {
  name: string;
  icon: string;
}

export type ProtocolsList = Record<Address, ProtocolData>;

export const STATIC_SERVER = "https://static.gearbox.finance";

export const GEARBOX_PROTOCOL_INFO: ProtocolData = {
  name: "Gearbox",
  icon: "/logo/logo_symbol.png",
};

const CURVE: ProtocolData = { name: "Curve", icon: "/protocols/curve.svg" };
const BALANCER: ProtocolData = {
  name: "Balancer",
  icon: "/protocols/balancer.svg",
};
const SUSHI: ProtocolData = {
  name: "Sushiswap",
  icon: "/protocols/sushi.svg",
};
const CONVEX: ProtocolData = { name: "Convex", icon: "/protocols/convex.svg" };
const YEARN: ProtocolData = { name: "Yearn", icon: "/protocols/yearn.svg" };
const LIDO: ProtocolData = { name: "Lido", icon: "/protocols/lido.svg" };
const MELLOW: ProtocolData = { name: "Mellow", icon: "/protocols/mellow.svg" };
const MAKER: ProtocolData = { name: "Maker", icon: "/protocols/maker.svg" };
const SKY: ProtocolData = { name: "Sky", icon: "/protocols/sky.svg" };
const CAMELOT: ProtocolData = {
  name: "Camelot",
  icon: "/protocols/camelot.svg",
};
const UNISWAP: ProtocolData = {
  name: "Uniswap",
  icon: "/protocols/uniswap.png",
};
const VELODROME: ProtocolData = {
  name: "Velodrome",
  icon: "/protocols/velodrome.svg",
};
const PENDLE: ProtocolData = {
  name: "Pendle",
  icon: "/protocols/pendle.svg",
};
const AAVE: ProtocolData = {
  name: "Aave",
  icon: "/protocols/aave.svg",
};

/**
 * Maps charts-server protocol names to display data.
 * Keys must match the `name` field returned by
 * `https://api.gear-dev.dev/v1/protocols?chainId=...`.
 */
export const KNOWN_PROTOCOLS: Record<string, ProtocolData> = {
  CreditFacade: GEARBOX_PROTOCOL_INFO,
  CreditManagerv1: GEARBOX_PROTOCOL_INFO,

  SAVINGS_CRVUSD_VAULT: CURVE,
  CURVE_GHO_USDE_POOL: CURVE,
  CURVE_USDE_USDC_POOL: CURVE,
  CURVE_LLAMA_THENA_POOL: CURVE,
  CURVE_tETH_wstETH_POOL: CURVE,
  CURVE_tETH_weETH_POOL: CURVE,
  CURVE_PUFETH_WSTETH_POOL: CURVE,
  FRAX3CRV_POOL: CURVE,
  CURVE_TRI_CRV_POOL: CURVE,
  CURVE_CVXETH_POOL: CURVE,
  CURVE_SDAI_SUSDE_POOL: CURVE,
  CURVE_FRAX_USDC_POOL: CURVE,
  CURVE_DOLA_CRVUSD_POOL: CURVE,
  CURVE_CRVUSD_USDC_POOL: CURVE,
  CURVE_CRVUSD_USDT_POOL: CURVE,
  CURVE_CRVUSD_FRAX_POOL: CURVE,
  GUSD3CRV_POOL: CURVE,
  CURVE_RETH_ETH_POOL: CURVE,
  CURVE_FRAX_USDE_POOL: CURVE,
  CURVE_EZETH_ETH_POOL: CURVE,
  CURVE_EZPZ_ETH_POOL: CURVE,
  CURVE_USDE_CRVUSD_POOL: CURVE,
  CURVE_USDE_USDC_POOL_ARB: CURVE,
  CURVE_USDE_DAI_POOL: CURVE,
  CURVE_USDL_USDC_POOL: CURVE,
  CURVE_DOLA_FRAXBP_POOL: CURVE,
  CURVE_GHO_CRVUSD_POOL: CURVE,
  CURVE_LBTC_WBTC_POOL: CURVE,
  CURVE_RLUSD_USDC_POOL: CURVE,
  CURVE_pzETH_stETH_POOL: CURVE,
  CURVE_3CRYPTO_POOL: CURVE,
  CURVE_TRIBTC_POOL: CURVE,
  CURVE_EBTC_WBTC_POOL: CURVE,
  CURVE_tBTC_WBTC_POOL: CURVE,
  CURVE_STETH_GATEWAY: CURVE,
  CURVE_SUSD_POOL: CURVE,
  "3CRV_POOL": CURVE,
  LUSD3CRV_POOL: CURVE,
  crvPlain3andSUSD_DEPOSIT: CURVE,

  BALANCER_VAULT: BALANCER,

  SUSHISWAP_ROUTER: SUSHI,

  CONVEX_FRAX_USDC_POOL: CONVEX,
  CONVEX_FRAX3CRV_POOL: CONVEX,
  CONVEX_LLAMA_THENA_POOL: CONVEX,
  CONVEX_LUSD3CRV_POOL: CONVEX,
  CONVEX_SUSD_POOL: CONVEX,
  CONVEX_3CRV_POOL: CONVEX,
  CONVEX_CRVUSD_USDT_POOL: CONVEX,
  CONVEX_CRVUSD_USDC_POOL: CONVEX,
  CONVEX_RLUSD_USDC_POOL: CONVEX,
  CONVEX_GUSD_POOL: CONVEX,
  CONVEX_CVXETH_POOL: CONVEX,
  CONVEX_STECRV_POOL: CONVEX,
  CONVEX_BOOSTER: CONVEX,

  YEARN_CURVE_STETH_VAULT: YEARN,
  YEARN_CURVE_FRAX_VAULT: YEARN,
  YEARN_DAI_VAULT: YEARN,
  YEARN_USDC_VAULT: YEARN,
  YEARN_WETH_VAULT: YEARN,

  LIDO_STETH_GATEWAY: LIDO,
  LIDO_WSTETH: LIDO,

  MELLOW_RESTAKING_VAULT: MELLOW,
  MELLOW_AMPHOR_VAULT: MELLOW,
  MELLOW_RE7_LABS_VAULT: MELLOW,
  MELLOW_STEAKHOUSE_VAULT: MELLOW,
  MELLOW_RENZO_VAULT: MELLOW,
  MELLOW_DECENTALIZED_VALIDATOR_VAULT: MELLOW,

  MAKER_DSR_VAULT: MAKER,
  DAI_USDS: MAKER,
  STAKED_USDS_VAULT: MAKER,

  SKY_STAKING_REWARDS: SKY,

  CAMELOT_V3_ROUTER: CAMELOT,

  UNISWAPV3_ROUTER: UNISWAP,
  UNISWAPV2_ROUTER: UNISWAP,

  VELODROME_CL_ROUTER: VELODROME,

  PENDLE_ROUTER: PENDLE,

  AAVE_WSTETH_VAULT: AAVE,

  STAKED_USDE_VAULT: { name: "Ethena", icon: "/protocols/ethena.svg" },
  WRAPPED_USDL_VAULT: { name: "USDL", icon: "/protocols/usdl.svg" },
  COINSHIFT_USDL_VAULT: { name: "Coinshift", icon: "/protocols/coinshift.svg" },
};

/**
 * Fetches the address -> protocol-meta map from the charts API and resolves
 * each entry through the local {@link KNOWN_PROTOCOLS} map.
 *
 * Failure is reported by the caller (we surface the error to the route hook so
 * the table can fall back to "Gearbox" for unknown protocol addresses).
 */
export async function loadProtocols(chainId: number): Promise<ProtocolsList> {
  const res = await fetch(
    `https://api.gear-dev.dev/v1/protocols?chainId=${chainId}`,
  );
  if (!res.ok) {
    throw new Error(`protocols fetch failed: HTTP ${res.status}`);
  }
  const json = (await res.json()) as {
    data: { protocol: Address; name: string }[];
  };
  const out: ProtocolsList = {};
  for (const { protocol, name } of json.data) {
    if (!protocol) continue;
    out[protocol.toLowerCase() as Address] =
      KNOWN_PROTOCOLS[name] ?? GEARBOX_PROTOCOL_INFO;
  }
  return out;
}

export const protocolIconUrl = (icon: string): string =>
  icon.startsWith("http") ? icon : `${STATIC_SERVER}${icon}`;
